import { useState, useCallback } from 'react';
import { getServiceById } from '../data/services';
import { simulatePayment } from '../utils/helpers';

export const useBooking = () => {
  const [currentStep, setCurrentStep] = useState('service');
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [paymentResult, setPaymentResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const selectService = useCallback((serviceId) => {
    const service = getServiceById(serviceId);
    setSelectedService(service);
    setSelectedDate(null);
    setSelectedTime('');
    setCurrentStep('datetime');
  }, []);

  const selectDateTime = useCallback((date, time) => {
    setSelectedDate(date);
    setSelectedTime(time);
  }, []);

  const updateCustomerDetails = useCallback((details) => {
    setCustomerDetails(prev => ({ ...prev, ...details }));
  }, []);

  const proceedToPayment = useCallback(() => {
    setCurrentStep('payment');
  }, []);

  const processPayment = useCallback(async () => {
    if (
      !selectedService ||
      !selectedDate ||
      !selectedTime ||
      !customerDetails.name?.trim() ||
      !customerDetails.email?.trim() ||
      !customerDetails.phone?.trim()
    ) {
      throw new Error('Missing required booking information');
    }

    setIsProcessing(true);
    
    try {
      const result = await simulatePayment(selectedService.price, customerDetails);
      setPaymentResult(result);
      setCurrentStep('confirmation');
      return result;
    } catch (error) {
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, [selectedService, selectedDate, selectedTime, customerDetails]);

  const getBookingSummary = useCallback(() => {
    if (!selectedService) return null;

    return {
      service: selectedService,
      date: selectedDate,
      time: selectedTime,
      customer: customerDetails,
      total: selectedService.price,
      payment: paymentResult
    };
  }, [selectedService, selectedDate, selectedTime, customerDetails, paymentResult]);

  const resetBooking = useCallback(() => {
    setCurrentStep('service');
    setSelectedService(null);
    setSelectedDate(null);
    setSelectedTime('');
    setCustomerDetails({
      name: '',
      email: '',
      phone: '',
      notes: ''
    });
    setPaymentResult(null);
    setIsProcessing(false);
  }, []);

  const goToStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  const canProceed = useCallback(() => {
    switch (currentStep) {
      case 'service':
        return !!selectedService;
      case 'datetime':
        return !!selectedDate && !!selectedTime;
      case 'details':
        return !!(customerDetails.name && customerDetails.email && customerDetails.phone);
      case 'payment':
        return true;
      case 'confirmation':
        return !!paymentResult;
      default:
        return false;
    }
  }, [currentStep, selectedService, selectedDate, selectedTime, customerDetails, paymentResult]);

  const getValidationErrors = useCallback(() => {
    const errors = {};

    switch (currentStep) {
      case 'datetime':
        if (!selectedDate) errors.date = 'Please select a date';
        if (!selectedTime) errors.time = 'Please select a time';
        break;
      case 'details':
        if (!customerDetails.name.trim()) errors.name = 'Name is required';
        if (!customerDetails.email.trim()) errors.email = 'Email is required';
        if (!customerDetails.phone.trim()) errors.phone = 'Phone is required';
        break;
      default:
        // No validation needed for other steps
        break;
    }

    return errors;
  }, [currentStep, selectedDate, selectedTime, customerDetails]);

  return {
    // State
    currentStep,
    selectedService,
    selectedDate,
    selectedTime,
    customerDetails,
    paymentResult,
    isProcessing,
    
    // Actions
    selectService,
    selectDateTime,
    updateCustomerDetails,
    proceedToPayment,
    processPayment,
    resetBooking,
    goToStep,
    
    // Helpers
    getBookingSummary,
    canProceed,
    getValidationErrors
  };
};
